import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { isPlausibleBirthDate } from "../utils/date.utils";
import { isAcceptablePhoneNumber, isCompletePhoneNumber } from "../utils/phone.utils";
import {
  CivilityEnum,
  NotificationPrefEnum,
  RoleEnum,
} from "../types/user.definition";
import { emergencyContactSchema } from "./emergency-contact.schema";
import { locationsArraySchema } from "./location.schema";

/** A phone field left empty, or holding only its country code ("+221"). */
export const isBlankPhoneNumber = (value?: string): boolean =>
  (value ?? "").replace(/\D/g, "").length <= 3;

type NotificationMethod = (typeof NotificationPrefEnum)[keyof typeof NotificationPrefEnum];

/**
 * MED-1687: which notification methods a new patient can pick, from the contacts typed so far.
 * With only a valid email, EMAIL is the only choice (forced); with only a complete phone, SMS.
 * With both, the user chooses; with neither, nothing can be picked yet.
 */
export const notificationMethodsFor = (
  email?: string,
  phoneNumber?: string,
): { available: NotificationMethod[]; forced: NotificationMethod | null } => {
  const emailOk = !!email?.trim() && z.email().safeParse(email.trim()).success;
  const phoneOk = !!phoneNumber && isCompletePhoneNumber(phoneNumber);
  if (emailOk && phoneOk) {
    return { available: [NotificationPrefEnum.EMAIL, NotificationPrefEnum.SMS], forced: null };
  }
  if (emailOk) return { available: [NotificationPrefEnum.EMAIL], forced: NotificationPrefEnum.EMAIL };
  if (phoneOk) return { available: [NotificationPrefEnum.SMS], forced: NotificationPrefEnum.SMS };
  return { available: [], forced: null };
};

/**
 * MED-1687: a patient may have an email, a phone number or both (phone-only accounts exist), so
 * neither is required on its own but at least one is. Both fields get the message.
 */
const requireEmailOrPhone =
  (t: TFunction) =>
  (data: { email?: string; phoneNumber?: string }, ctx: z.RefinementCtx) => {
    if (!data.email?.trim() && isBlankPhoneNumber(data.phoneNumber)) {
      const message = t("common.validation.emailOrPhoneRequired");
      ctx.addIssue({ code: "custom", message, path: ["email"] });
      ctx.addIssue({ code: "custom", message, path: ["phoneNumber"] });
    }
  };

export const patientFormSchema = (t: TFunction) =>
  z
    .object({
      email: z.union([z.literal(""), z.email(t("common.validation.invalidEmail"))]),
      firstName: z.string().nonempty(t("common.validation.required")),
      lastName: z.string().nonempty(t("common.validation.required")),
      dateOfBirth: z
        .string()
        .nonempty(t("common.validation.required"))
        .refine((date) => {
          const parsedDate = new Date(date);
          return !isNaN(parsedDate.getTime()) && parsedDate < new Date();
        }, t("common.validation.dateOfBirthInThePast"))
        .refine((date) => isPlausibleBirthDate(date), t("common.validation.invalidDateOfBirth")),
      civility: z.enum([CivilityEnum.MR, CivilityEnum.MRS], {
        message: t("common.validation.required"),
      }),
      phoneNumber: z.string().refine(
        (val) => isBlankPhoneNumber(val) || isCompletePhoneNumber(val),
        { message: t("common.validation.invalidPhoneNumber") }
      ),

      roleName: z.enum(
        [
          RoleEnum.PATIENT,
          RoleEnum.DOCTOR,
          RoleEnum.MANAGER,
          RoleEnum.ADMIN,
          RoleEnum.SUPER_ADMIN,
        ],
        {
          message: t("common.validation.required"),
        }
      ),
      locations: locationsArraySchema(t),
      preferredNotificationMethods: z
        .array(z.enum([NotificationPrefEnum.EMAIL, NotificationPrefEnum.SMS]), {
          message: t("common.validation.required"),
        })
        // MED-1653: the BFF refused an empty list after the request was sent.
        .min(1, t("common.validation.selectAtLeastOne")),
      bloodType: z.string().optional().or(z.literal("")),
      emergencyContacts: z.array(emergencyContactSchema(t)),
    })
    // MED-1687: no password at creation, the API sends the patient a temporary one
    .superRefine(requireEmailOrPhone(t));

export const usePatientFormSchema = () => {
  const { t } = useTranslation();
  return patientFormSchema(t);
};

/**
 * @param initialPhoneNumber the patient's stored number: kept as is, it only has to pass the old
 * rule, so an older short number does not block saving other fields (MED-1653).
 */
export const editPatientFormSchema = (
  t: TFunction,
  initialPhoneNumber?: string,
  existingEmergencyPhones: readonly string[] = [],
) =>
  z
    .object({
      email: z.union([z.literal(""), z.email(t("common.validation.invalidEmail"))]),
      password: z.string().optional(),
      confirmPassword: z.string().optional(),
      firstName: z.string().nonempty(t("common.validation.required")),
      lastName: z.string().nonempty(t("common.validation.required")),
      dateOfBirth: z
        .string()
        .nonempty(t("common.validation.required"))
        .refine((date) => {
          const parsedDate = new Date(date);
          return !isNaN(parsedDate.getTime()) && parsedDate < new Date();
        }, t("common.validation.dateOfBirthInThePast"))
        .refine((date) => isPlausibleBirthDate(date), t("common.validation.invalidDateOfBirth")),
      civility: z.enum([CivilityEnum.MR, CivilityEnum.MRS], {
        message: t("common.validation.required"),
      }),
      phoneNumber: z.string().refine(
        (val) => isBlankPhoneNumber(val) || isAcceptablePhoneNumber(val, initialPhoneNumber),
        { message: t("common.validation.invalidPhoneNumber") }
      ),

      roleName: z.enum(
        [
          RoleEnum.PATIENT,
          RoleEnum.DOCTOR,
          RoleEnum.MANAGER,
          RoleEnum.ADMIN,
          RoleEnum.SUPER_ADMIN,
        ],
        {
          message: t("common.validation.required"),
        }
      ),
      locations: locationsArraySchema(t),
      preferredNotificationMethods: z
        .array(z.enum([NotificationPrefEnum.EMAIL, NotificationPrefEnum.SMS]), {
          message: t("common.validation.required"),
        })
        // MED-1653: the BFF refused an empty list after the request was sent.
        .min(1, t("common.validation.selectAtLeastOne")),
      bloodType: z.string().optional(),
      emergencyContacts: z.array(emergencyContactSchema(t, existingEmergencyPhones)),
    })
    .superRefine(requireEmailOrPhone(t))
    .refine(
      (data) => {
        // Only validate password match if password is provided
        if (data.password && data.password.trim() !== "") {
          return data.password === data.confirmPassword;
        }
        return true;
      },
      {
        message: t("common.validation.passwordMismatch"),
        path: ["confirmPassword"],
      }
    );

export const useEditPatientFormSchema = (
  initialPhoneNumber?: string,
  existingEmergencyPhones: readonly string[] = [],
) => {
  const { t } = useTranslation();
  return editPatientFormSchema(t, initialPhoneNumber, existingEmergencyPhones);
};

export type PatientFormSchema = z.infer<
  ReturnType<typeof usePatientFormSchema>
>;

export type EditPatientFormSchema = z.infer<
  ReturnType<typeof useEditPatientFormSchema>
>;
